import { MessageSquare, SendHorizontal, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { LIMITS } from '../../shared/protocol';
import { formatTime } from '../lib/format';
import { splitLinks } from '../lib/links';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';

export function ChatPanel({ session, onClose }: { session: MeetingSession; onClose(): void }) {
  const s = useStore(session.store);
  const [to, setTo] = useState<'everyone' | string>('everyone');
  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const selfId = s.selfId!;
  const amHost = s.participants[selfId]?.role === 'host';
  const restricted = !s.roomSettings.allowChat && !amHost;
  const others = Object.values(s.participants).filter((p) => p.id !== selfId);
  const recipients = restricted ? others.filter((p) => p.role === 'host') : others;

  useEffect(() => {
    session.setChatOpen(true);
    return () => session.setChatOpen(false);
  }, [session]);

  // Recipient left or chat got restricted: fall back to a valid target.
  useEffect(() => {
    if (to !== 'everyone' && !recipients.some((p) => p.id === to)) setTo(restricted && recipients[0] ? recipients[0].id : 'everyone');
    if (to === 'everyone' && restricted && recipients[0]) setTo(recipients[0].id);
  }, [to, recipients, restricted]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [s.messages.length]);

  const send = () => {
    if (!text.trim()) return;
    if (session.sendChat(to, text)) {
      setText('');
      stickToBottom.current = true;
    }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const canSend = !(restricted && recipients.length === 0);

  return (
    <section className="panel" aria-label="Chat de la reunión">
      <div className="panel-header">
        <span>Chat de la reunión</span>
        <button type="button" className="icon-btn" aria-label="Cerrar chat" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      <div
        ref={listRef}
        className="panel-body chat-list"
        aria-live="polite"
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
      >
        {s.messages.length === 0 ? (
          <div className="chat-empty">
            <MessageSquare size={32} />
            <p>Los mensajes que envíes a «Todos» los verán todos los participantes. Solo verás los mensajes enviados desde que entraste.</p>
          </div>
        ) : (
          s.messages.map((m) => {
            const mine = m.from === selfId;
            const isPrivate = m.to !== 'everyone';
            const peer = mine ? m.toName : 'ti';
            return (
              <div key={m.id} className={`chat-msg ${mine ? 'mine' : ''} ${isPrivate ? 'private' : ''}`}>
                <span className="chat-meta">
                  <strong>{mine ? 'Tú' : m.fromName}</strong>
                  {isPrivate && <span className="private"> · en privado a {peer}</span>} · {formatTime(m.at)}
                </span>
                <div className="chat-bubble">
                  {splitLinks(m.text).map((part, i) =>
                    part.href ? (
                      <a key={i} href={part.href} target="_blank" rel="noopener noreferrer nofollow">
                        {part.text}
                      </a>
                    ) : (
                      part.text
                    ),
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="chat-compose">
        {restricted && <span className="hint">El anfitrión ha limitado el chat: solo puedes escribirle a él.</span>}
        <label className="chat-to">
          Para:
          <select className="select" value={to} onChange={(e) => setTo(e.target.value)} disabled={!canSend}>
            {!restricted && <option value="everyone">Todos</option>}
            {recipients.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.role === 'host' ? ' (anfitrión)' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="chat-input-row">
          <textarea
            className="textarea"
            rows={1}
            placeholder={canSend ? 'Escribe un mensaje…' : 'El chat está desactivado'}
            value={text}
            maxLength={LIMITS.chatLength}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={!canSend}
            aria-label="Mensaje"
          />
          <button type="button" className="icon-btn" aria-label="Enviar mensaje" onClick={send} disabled={!text.trim() || !canSend}>
            <SendHorizontal size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
